import {
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportTableCell,
  ScalingButtonGroup,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { LedgerPaymentSectionProps } from '@/features/mold/models/ledger';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import {
  getContractAmountOrExpendituresTitle,
  getContractName,
  getPaymentAmountBreakdownTableData,
  paymentNumberConverter,
  paymentValueConverter,
  shouldShowPaymentGraph,
} from '@/features/project/review-sheet/utils/payment';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { useLocaledAmount } from '@/providers';
import { FALLBACK_TEXT, SPACE_TEXT, formatValueByUnit } from '@/utils';
import classNames from 'classnames';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'reactflow';
import {
  LEDGER_BORDER,
  LEDGER_RIGHT_ALIGNED_STYLE,
  type TableRowType,
  formatBidRate,
  getEmptySectionTableDataByHeader,
  getEmptyTableDataByHeader,
} from '../../../utils';
import { PaymentGraphLayout } from '../../flow';
import styles from './LedgerPrint.module.scss';
import { TableContent } from './TableContent';

const printHiddenClass = classNames(styles.hidden_print);

export const PaymentSection: FC<LedgerPaymentSectionProps> = ({
  payments,
  paymentGroups,
  paymentGraph,
  project,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();

  const isKsStandardAfter2025 = useMemo(
    () =>
      project?.sheetType === 'KS' &&
      project.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project),
    [project],
  );

  const paymentGroupTableData = useMemo<TableRowType>(() => {
    const topHeader: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          text: t('payment-group-name'),
          ratio: 3.3,
          textWrapperStyle: { width: 0, whiteSpace: 'nowrap' },
          cellStyle: { borderLeft: 'none' },
        },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('total-expenditure'),
          ratio: 0.9,
        },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('payee-count'),
          ratio: 0.6,
        },
        { text: t('payment-group-overview'), ratio: 3 },
      ],
    };

    const paymentHeader: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.3,
          cellStyle: {
            borderBottom: 0,
            background: 'white',
            borderLeft: 'none',
          },
        },
        { text: t('payment-name'), ratio: 3 },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('amount-of-expenditures'),
          ratio: 0.9,
        },
        { text: t('corporate-number'), ratio: 3.6 },
      ],
    };
    const getContractHeader = (
      paymentType: PaymentType,
    ): TableRowType[number] => ({
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.3,
          cellStyle: {
            borderBottom: 0,
            background: 'white',
            borderLeft: 'none',
          },
        },
        {
          isSpace: true,
          ratio: 0.3,
          cellStyle: {
            borderBottom: 0,
            background: 'white',
          },
        },
        {
          text: t('table-payment-contract-overview-label'),
          ratio: 2.7,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: getContractAmountOrExpendituresTitle(paymentType),
          ratio: 0.9,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
        ...(!isKsStandardAfter2025
          ? [
              {
                text: t('number-of-applicants-table-title'),
                ratio: 0.6,
                textWrapperStyle: { justifyContent: 'center' },
                cellStyle: { borderTop: LEDGER_BORDER },
              },
              {
                text: t('bid-rate'),
                ratio: 0.6,
                cellStyle: { borderTop: LEDGER_BORDER },
              },
              {
                text: t('payment-reason-title'),
                ratio: 2.4,
                cellStyle: { borderTop: LEDGER_BORDER },
              },
            ]
          : [
              {
                isSpace: true,
                ratio: 3.6,
                cellStyle: {
                  borderTop: LEDGER_BORDER,
                  borderBottom: 0,
                  background: 'white',
                },
              },
            ]),
      ],
    });

    const data = paymentGroups?.flatMap<TableRowType[number]>(
      (data, dataIdx, arr) => {
        const isLastDataIdx = dataIdx === arr.length - 1;

        const topData: TableRowType = [
          {
            row: [
              {
                text: data.displayCode,
                ratio: 0.3,
                cellStyle: {
                  borderBottom:
                    !data.payments.length && !isLastDataIdx ? LEDGER_BORDER : 0,
                  borderLeft: 'none',
                },
                textWrapperStyle: { justifyContent: 'center' },
              },
              {
                text: data.name,
                ratio: 3,
                textWrapperStyle: { borderLeft: 0, minHeight: '3em' },
              },
              {
                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                ratio: 0.9,
                text: paymentValueConverter(
                  data.totalAmount,
                  unit,
                  !!devtoolState?.isDebugMode,
                  data.negativeTotalAmountCount,
                ),
              },
              {
                text: paymentNumberConverter(data.paymentCount),
                ratio: 0.6,
                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
              },
              { text: data.overview, ratio: 3 },
            ],
          },
        ];

        const paymentData = ((): TableRowType => {
          return data.payments.flatMap<TableRowType[number]>(
            (payment, paymentIdx) => {
              const isLastPayment = paymentIdx === data.payments.length - 1;
              const contractCount = payment.contracts.length;
              const borderBottom =
                contractCount >= 1 || isLastDataIdx ? 'none' : LEDGER_BORDER;
              return [
                paymentHeader,
                {
                  row: [
                    {
                      isSpace: true,
                      cellStyle: {
                        borderBottom:
                          contractCount === 0 && !isLastDataIdx && isLastPayment
                            ? LEDGER_BORDER
                            : 'none',
                        borderLeft: 'none',
                      },
                    },
                    {
                      text: payment.name,
                      textWrapperStyle: { minHeight: '3em' },
                      cellStyle: { borderBottom },
                    },
                    {
                      textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                      text: paymentValueConverter(
                        payment.totalContractAmount,
                        unit,
                        !!devtoolState?.isDebugMode,
                        payment.negativeTotalContractAmountCount,
                      ),
                      cellStyle: { borderBottom },
                    },
                    {
                      text: payment.corporateNumber,
                      cellStyle: { borderBottom },
                    },
                  ].map((v, i) => ({
                    ...v,
                    ratio: paymentHeader.row[i]?.ratio,
                  })),
                },
                ...(payment.contracts.length === 0
                  ? []
                  : [
                      getContractHeader(payment.type),
                      ...payment.contracts.map<TableRowType[number]>(
                        (contract, contractIdx, arr) => {
                          const isLast = contractIdx === arr.length - 1;
                          return {
                            row: [
                              {
                                isSpace: true,
                                cellStyle: {
                                  borderBottom:
                                    isLast && isLastPayment && !isLastDataIdx
                                      ? LEDGER_BORDER
                                      : 0,
                                  borderLeft: 'none',
                                },
                              },
                              {
                                isSpace: true,
                                cellStyle: {
                                  borderBottom:
                                    isLast && isLastPayment && isLastDataIdx
                                      ? 0
                                      : isLast
                                        ? LEDGER_BORDER
                                        : 0,
                                },
                              },
                              {
                                text: `${contract.overview}\n${
                                  getContractName(
                                    contract.contractMethod,
                                    contract.contractMethodDescription,
                                  ) ?? FALLBACK_TEXT
                                } `,
                                cellStyle: { paddingLeft: 0 },
                              },
                              {
                                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                                text: paymentValueConverter(
                                  contract.amount,
                                  unit,
                                  false,
                                  undefined,
                                ),
                              },
                              ...(!isKsStandardAfter2025
                                ? [
                                    {
                                      text: paymentNumberConverter(
                                        contract.numberOfApplicants,
                                      ),
                                      textWrapperStyle:
                                        LEDGER_RIGHT_ALIGNED_STYLE,
                                    },
                                    {
                                      text: formatBidRate(contract.bidRate),
                                      textWrapperStyle:
                                        LEDGER_RIGHT_ALIGNED_STYLE,
                                    },
                                    { text: contract.reason },
                                  ]
                                : [
                                    {
                                      isSpace: true,
                                      cellStyle: {
                                        ...(!isLast && {
                                          borderBottom: 0,
                                        }),
                                      },
                                    },
                                  ]),
                            ].map((v, i) => ({
                              ...v,
                              ratio: getContractHeader(payment.type).row[i]
                                ?.ratio,
                            })),
                          };
                        },
                      ),
                    ]),
              ];
            },
          );
        })();
        return [topHeader, ...topData, ...paymentData];
      },
    );

    return data?.length ? data : getEmptyTableDataByHeader(topHeader);
  }, [paymentGroups, t, unit, isKsStandardAfter2025, devtoolState]);

  const amountBreakdownSectionTableData = useMemo<{
    header: ReportSectionItemTableProps['header'];
    data: ReportSectionItemTableProps['data'];
  }>(() => {
    const header: ReportSectionItemTableProps['header'] = [
      {
        type: 'text',
        value: SPACE_TEXT,
        ratio: 0.3,
      },
      { type: 'text', value: t('payment-name'), ratio: 2 },
      { type: 'text', value: t('payment-contract-overview'), ratio: 1.5 },
      { type: 'text', value: t('payment-amount-breakdown-name'), ratio: 1 },
      { type: 'text', value: t('payment-amount-breakdown-purpose'), ratio: 2 },
      {
        type: 'text',
        value: t('payment-amount-breakdown-amount'),
        align: 'center',
        ratio: 1,
      },
    ];

    const dataSource = getPaymentAmountBreakdownTableData(paymentGroups ?? []);

    const data: ReportSectionItemTableProps['data'] = dataSource.flatMap(
      (data) => {
        const { children, ...resetData } = data;
        const createRow = (d: Partial<typeof resetData>) => ({
          row: [
            {
              type: 'text',
              value: d.displayCode,
              align: 'center',
            },
            { type: 'text', value: d.paymentName },
            { type: 'text', value: d.contractOverview },
            { type: 'text', value: d.name },
            { type: 'text', value: d.purpose },
            {
              type: 'text',
              value: formatValueByUnit(d.amount, { operation: 'round', unit }),
              align: 'right',
            },
          ] as ReportTableCell[],
        });
        return [createRow(resetData), ...(children ?? []).map(createRow)];
      },
    );

    return {
      header,
      data: data.length ? data : getEmptySectionTableDataByHeader(header),
    };
  }, [paymentGroups, t, unit]);

  const paymentTableData = useMemo<TableRowType>(() => {
    const topHeader: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.2,
          cellStyle: { borderLeft: 'none' },
        },
        { text: t('contractor-name'), ratio: 2 },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('contract-amount'),
          ratio: 0.6,
        },
        { text: t('corporate-number'), ratio: 2.4 },
      ],
    };

    const getContractHeader = (
      paymentType: PaymentType,
    ): TableRowType[number] => ({
      isHeader: true,
      row: [
        {
          isSpace: true,
          ratio: 0.2,
          cellStyle: {
            borderBottom: 0,
            background: 'white',
            borderLeft: 'none',
          },
        },
        {
          isSpace: true,
          ratio: 0.1,
          cellStyle: { borderBottom: 0, background: 'white' },
        },
        {
          text: t('table-payment-contract-overview-label'),
          ratio: 1.9,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
        {
          text: getContractAmountOrExpendituresTitle(paymentType),
          ratio: !isKsStandardAfter2025 ? 0.6 : 3,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('number-of-applicants-table-title'),
          ratio: 0.4,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('bid-rate'),
          ratio: 0.4,
          cellStyle: { borderTop: LEDGER_BORDER },
          textWrapperStyle: { justifyContent: 'center' },
        },
        {
          text: t('payment-reason-title'),
          ratio: 1.6,
          cellStyle: { borderTop: LEDGER_BORDER },
        },
      ],
    });

    const data = payments?.flatMap<TableRowType[number]>((data, dataIdx) => {
      const isLastDataIdx = dataIdx === payments.length - 1;
      const contractCount = data.contracts.length;
      const borderBottom =
        contractCount >= 1 || isLastDataIdx ? 'none' : LEDGER_BORDER;
      const blockBody: TableRowType = [
        {
          row: [
            {
              textWrapperStyle: {
                justifyContent: 'center',
                minHeight: '3em',
              },
              cellStyle: {
                borderBottom,
                borderLeft: 'none',
              },
              text: paymentGroups?.find(
                (paymentGroup) => paymentGroup.id === data.paymentGroupId,
              )?.displayCode,
            },
            {
              text: data.name,
              cellStyle: { borderBottom },
            },
            {
              cellStyle: { borderBottom },
              textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
              text: paymentValueConverter(
                data.totalContractAmount,
                unit,
                !!devtoolState?.isDebugMode,
                data.negativeTotalContractAmountCount,
              ),
            },
            { text: data.corporateNumber, cellStyle: { borderBottom } },
          ].map((v, idx) => ({ ...v, ratio: topHeader.row[idx]?.ratio })),
        },
      ];

      const contractData = ((): TableRowType => {
        return data.contracts.flatMap<TableRowType[number]>(
          (contract, contractIdx) => {
            const isLast = contractIdx === data.contracts.length - 1;
            const header = getContractHeader(data.type);

            return [
              ...(contractIdx === 0 ? [header] : []),
              {
                row: [
                  {
                    isSpace: true,
                    cellStyle: {
                      borderBottom:
                        isLast && !isLastDataIdx ? LEDGER_BORDER : 0,
                      borderLeft: 'none',
                    },
                  },
                  {
                    isSpace: true,
                    cellStyle: {
                      borderBottom:
                        isLast && !isLastDataIdx ? LEDGER_BORDER : 0,
                    },
                  },
                  {
                    text: `${contract.overview}\n${
                      getContractName(
                        contract.contractMethod,
                        contract.contractMethodDescription,
                      ) ?? FALLBACK_TEXT
                    } `,
                  },
                  {
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                    text: paymentValueConverter(
                      contract.amount,
                      unit,
                      false,
                      undefined,
                    ),
                  },
                  {
                    text: contract.numberOfApplicants,
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                  },
                  {
                    text: formatBidRate(contract.bidRate),
                    textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                  },
                  { text: contract.reason },
                ].map((v, idx) => ({ ...v, ratio: header.row[idx]?.ratio })),
              },
            ];
          },
        );
      })();
      return [topHeader, ...blockBody, ...contractData];
    });

    return data?.length ? data : getEmptyTableDataByHeader(topHeader);
  }, [paymentGroups, payments, t, unit, isKsStandardAfter2025, devtoolState]);

  if (!project || !paymentGroups || !paymentGraph) {
    return null;
  }

  const isShowPaymentGraph = !!(
    project.fiscalYear &&
    shouldShowPaymentGraph(project.fiscalYear, project.sheetType)
  );

  return (
    <ReportSection
      title={t('payment')}
      items={[
        ...(isShowPaymentGraph
          ? [
              {
                wrapperStyle: { borderLeft: 'none' },
                item: (
                  <ReportSectionItemContent
                    wrapperStyle={{
                      flexDirection: 'column',
                      borderBottom: 'none',
                      pageBreakAfter: 'always',
                    }}
                    labelStyle={{
                      borderLeft: 'none',
                      width: '100%',
                      borderRight: 'none',
                      borderBottom: LEDGER_BORDER,
                    }}
                    label={t('payment-edge')}
                    value={
                      <PaymentGraphLayout
                        hideBorder
                        projectId={project.id}
                        ministryId={project.ministryId}
                        paymentIndirectCosts={project.paymentIndirectCosts}
                        paymentGroups={paymentGroups}
                        paymentNodesMeta={paymentGraph.nodesMeta}
                        paymentEdges={paymentGraph.edges}
                        panelNode={() => {
                          return (
                            <Panel
                              className={printHiddenClass}
                              position="top-right"
                            >
                              <ScalingButtonGroup />
                            </Panel>
                          );
                        }}
                      />
                    }
                  />
                ),
              },
            ]
          : []),
        {
          subtitle: isKsStandardAfter2025
            ? t(
                'ledger-payment-list-with-unit-expenditure-to-each-block-in-previous-year',
              )
            : t('ledger-payment-list-with-unit'),
          item: (
            <TableContent defaultDataLength={3} data={paymentGroupTableData} />
          ),
        },
        {
          subtitle: isKsStandardAfter2025
            ? t(
                'payment-amount-breakdown-expenditure-from-each-block-in-previous-year-with-unit',
              )
            : t('payment-amount-breakdown-with-unit'),
          item: (
            <ReportSectionItemContent
              isTable
              isLast
              value={
                <ReportSectionItemTable
                  header={amountBreakdownSectionTableData.header}
                  data={amountBreakdownSectionTableData.data}
                />
              }
            />
          ),
          // NOTE: 2025年以降KSの帳票でデータがない場合に線が途切れることを防ぐために実装。空の場合１行は表示するため最小は１
          // データが存在する場合flexを当てるとレイアウトが崩れる
          innerStyle:
            amountBreakdownSectionTableData.data.length === 1
              ? { display: 'flex' }
              : undefined,
        },
        ...(project.sheetType === 'KS'
          ? []
          : [
              {
                subtitle: t('ledger-payment-contractor-list-with-unit'),
                item: (
                  <TableContent defaultDataLength={3} data={paymentTableData} />
                ),
              },
            ]),
      ]}
    />
  );
};
